import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { profileAPI } from '../api';
import './Tutorial.css';

// Steps are keyed by the name persisted in the user's profile (tutorial_step),
// so existing keys must stay stable. Feature-flagged steps (divination,
// boosters) are only included — and only linked into the step chain — when the
// server reports the feature as enabled.
const buildTutorialSteps = ({ divinationEnabled, boostersEnabled }) => ({
  welcome: {
    title: 'Welcome to Aesthetic Legends! 🎴',
    content: `Welcome! Let me guide you through all the features of the game.

**Fragments** are the main currency used to generate cards:
• **Random fragments** generate random cards (any theme)
• **Theme fragments** generate cards from specific themes
• You start with **2000 random fragments** (enough for 20 cards!) and **1000 wishes**`,
    nextStep: 'tokens',
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: null
  },
  tokens: {
    title: 'Your Collection 🎴',
    content: `Click the **"Collection"** button in the navigation menu to access your cards, dates, videos, tokens, and stats.`,
    nextStep: 'tokens_tab',
    highlightElement: 'nav-collection',
    requiresNavigation: 'collection',
    autoAdvanceOn: null
  },
  tokens_tab: {
    title: 'Tokens & Fragments 💎',
    content: `Open the **"💎 Tokens"** tab to see your fragments and mutation items.

Card cost depends on rarity: Common (100), Rare (200), Epic (350), Legendary (700).

Click the tab, then click **Next**.`,
    nextStep: 'generate_card',
    highlightElement: 'tab-tokens',
    requiresNavigation: false,
    autoAdvanceOn: null
  },
  generate_card: {
    title: 'Generate Your First Card 🎴',
    content: `Now click the **"Generate Card (100)"** button in the **Random Fragments** section to create your first card.`,
    nextStep: 'generate_card_modal',
    highlightElement: 'btn-generate-card',
    requiresNavigation: false,
    autoAdvanceOn: 'modal-open'
  },
  generate_card_modal: {
    title: 'Confirm Generation ⚡',
    content: `Perfect! Click the **"Generate"** button in this window to start creating your card.

Card generation takes a few minutes.

Tip: cards are generated as **female** by default — you can switch to male cards anytime in your **Profile** settings.`,
    nextStep: 'expeditions',
    highlightElement: 'btn-confirm-generate',
    requiresNavigation: false,
    autoAdvanceOn: 'modal-close'
  },
  expeditions: {
    title: 'Expeditions While You Wait 🗺️',
    content: `While your card generates, let's explore **Expeditions**!

Click **"Expedition"** in the navigation to continue.

Expeditions let you send cards on quests to earn:
• Fragments (to generate more cards)
• Wishes (for creating dates)
• XP (to level up cards)
• Mutation items (to modify characteristics)

You can run up to **5 expeditions simultaneously**!`,
    nextStep: 'choose_expedition',
    highlightElement: 'nav-expeditions',
    requiresNavigation: 'expeditions-hub',
    autoAdvanceOn: null
  },
  choose_expedition: {
    title: 'Choose Find Companions 🗺️',
    content: `Click on the **"🔍 Find Companions"** expedition to open its details.

This special expedition doesn't require any cards and lets you earn fragments and wishes while you wait for your first card to generate!`,
    nextStep: 'start_expedition',
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: 'modal-open'
  },
  start_expedition: {
    title: 'Start the Quest 🚀',
    content: `Perfect! Now click **"Start Quest"** to begin your first expedition.

It takes 30 minutes to complete and will earn you fragments and wishes.`,
    nextStep: 'my_cards',
    highlightElement: 'btn-start-expedition',
    requiresNavigation: false,
    autoAdvanceOn: 'modal-close'
  },
  my_cards: {
    title: 'Check Your Cards 🎴',
    content: `Let's navigate back to **"Collection"** to see if your first card generation is complete!

Your cards appear in the **"🎴 My Cards"** tab.`,
    nextStep: 'dates_intro',
    highlightElement: 'nav-collection',
    requiresNavigation: 'collection',
    autoAdvanceOn: null
  },
  dates_intro: {
    title: 'Dates Feature 💕',
    content: `Great! Now let me explain **Dates**:

Dates let you create custom romantic scenes with your cards using **Wishes**.

To create a date:
• Click **"💝 New Date"** on one of your cards
• Select wishes to customize the scene
• Optionally pick a **Style** to change the artistic look
• Click **"Create Date"**

Each wish adds something special to the date image. Date generations are limited per week — the 🎨 counter at the top shows how many you have left.`,
    nextStep: 'try_date',
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: null
  },
  try_date: {
    title: 'Try Creating a Date 🌹',
    content: `You already have **1000 wishes** to get started!

Feel free to try creating a date when you're ready. Just click on a card and select **"New Date"**.

You can also turn your favorite dates into short **videos** — you start with 10 video tokens.`,
    nextStep: 'minigame',
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: null
  },
  minigame: {
    title: 'Mini games 🎮',
    content: `Navigate to **"Mini games"** to play games and compete with others!

You can play **"Prompt Guess"** to earn wishes and participate in weekly **Competitions** for exclusive rewards.`,
    nextStep: 'play_minigame',
    highlightElement: 'nav-minigames',
    requiresNavigation: 'minigames',
    autoAdvanceOn: null
  },
  play_minigame: {
    title: 'Play & Compete 🎮',
    content: `Great! Now you can switch between **🎮 Prompt Guess** and **🏆 Competition** using the tabs.

• **Prompt Guess**: guess which wish was used to create a date image — correct guesses earn wishes and build your streak!
• **Competition**: submit your best dates to weekly themed competitions and vote on other players' entries. Participating earns **Lumis**, which you can spend in the **"✨ Shop"** tab on exclusive rewards.

Click **Next** when ready to continue.`,
    nextStep: divinationEnabled ? 'divination' : 'wish_dictionary',
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: null
  },
  ...(divinationEnabled ? {
    divination: {
      title: 'The Divination Chamber 🔮',
      content: `There's also a **"🔮 Divination"** tab — the Divination Chamber, a mystic minigame where you place cards on spreads to win exclusive rewards.

It has its own tutorial inside that teaches you how to play, so give it a try when you're ready!

Click **Next** to continue.`,
      nextStep: 'wish_dictionary',
      highlightElement: 'tab-divination',
      requiresNavigation: false,
      autoAdvanceOn: null
    }
  } : {}),
  wish_dictionary: {
    title: 'Reference Library 📖',
    content: `Navigate to **"Reference"** to browse all available wishes, themes, styles, and community presets!

This is your complete reference guide with examples and inspiration for creating amazing dates.`,
    nextStep: 'explore_wish_dictionary',
    highlightElement: 'nav-reference',
    requiresNavigation: 'reference',
    autoAdvanceOn: null
  },
  explore_wish_dictionary: {
    title: 'Browse Reference Library 📖',
    content: `You can explore:
• **📖 Wish Dictionary** - Examples of what each wish does
• **🎨 Theme Dictionary** - Examples of different themes
• **🖼️ Style References** - The artistic styles you can apply to your dates
• **📋 Community Presets** - Pre-made wish combinations shared by other players

Browse through the reference library, then click **Next** to continue.`,
    nextStep: 'galleries',
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: null
  },
  galleries: {
    title: 'Galleries 🖼️',
    content: `Click **"Galleries"** in the navigation.

Galleries let you organize your favorite dates into collections and share them with the community.`,
    nextStep: boostersEnabled ? 'boosters' : 'trades',
    highlightElement: 'nav-galleries',
    requiresNavigation: 'galleries',
    autoAdvanceOn: null
  },
  ...(boostersEnabled ? {
    boosters: {
      title: 'Booster Packs 🎴',
      content: `Click **"Boosters"** in the navigation.

Boosters are card packs you can earn from competitions and gifts. Your unopened packs wait here — open one for a little ceremony and new cards for your collection!`,
      nextStep: 'trades',
      highlightElement: 'nav-boosters',
      requiresNavigation: 'boosters',
      autoAdvanceOn: null
    }
  } : {}),
  trades: {
    title: 'Trading Cards 🔄',
    content: `Finally, click **"Trade"** to trade cards with other players!`,
    nextStep: 'explore_trades',
    highlightElement: 'nav-trades',
    requiresNavigation: 'trades',
    autoAdvanceOn: null
  },
  explore_trades: {
    title: 'Explore Trading 🔄',
    content: `Build your collection by:
• Listing cards you want to trade
• Browsing other players' trades
• Making offers
• Completing exchanges

Take a look at the trading system, then click **Next** to complete the tutorial!`,
    nextStep: 'tutorial_complete',
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: null
  },
  tutorial_complete: {
    title: 'Tutorial Complete! 🎉',
    content: `Congratulations! You've learned all the major features of Aesthetic Legends!

You're now ready to:
• Generate and collect cards
• Send cards on expeditions
• Create amazing dates with wishes and styles
• Play Prompt Guess and compete in weekly competitions
• Showcase your dates in galleries
• Trade with other players

Have fun and enjoy the game!`,
    nextStep: null,
    highlightElement: null,
    requiresNavigation: false,
    autoAdvanceOn: null
  }
});

// Selectors matched by the modal-open / modal-close auto-advance detector.
// Must cover every modal the tutorial flow opens: ConfirmModal (.modal-backdrop)
// and the expedition details modal (.expedition-modal-overlay).
// Delays kept from the previous polling implementation.
const HIGHLIGHT_SETTLE_MS = 300;
const MODAL_OPEN_ADVANCE_MS = 500;
const MODAL_CLOSE_ADVANCE_MS = 300;
const MODAL_OPEN_FIRST_CHECK_MS = 300;
const MODAL_CLOSE_FIRST_CHECK_MS = 500;

const sameBounds = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
};

const MODAL_SELECTORS =
  '.modal, .modal-overlay, .modal-backdrop, .expedition-modal-overlay, [role="dialog"], .MuiModal-root';

function Tutorial({
  currentPage,
  onNavigate,
  userProfile,
  onUpdateProfile,
  divinationEnabled = false,
  boostersEnabled = false
}) {
  const tutorialSteps = useMemo(
    () => buildTutorialSteps({ divinationEnabled, boostersEnabled }),
    [divinationEnabled, boostersEnabled]
  );
  const [currentStep, setCurrentStep] = useState('welcome');
  const [isVisible, setIsVisible] = useState(true);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [highlightBounds, setHighlightBounds] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const tutorialRef = useRef(null);
  // Watches the DOM for the modal an auto-advancing step waits on.
  const autoAdvanceObserverRef = useRef(null);

  const stopAutoAdvanceObserver = () => {
    if (autoAdvanceObserverRef.current) {
      autoAdvanceObserverRef.current.disconnect();
      autoAdvanceObserverRef.current = null;
    }
  };

  // Initialize tutorial from user profile
  useEffect(() => {
    if (userProfile) {
      if (userProfile.tutorial_completed) {
        setIsVisible(false);
      } else if (userProfile.tutorial_step && userProfile.tutorial_step !== '') {
        setCurrentStep(userProfile.tutorial_step);
      } else {
        // Start tutorial
        setCurrentStep('welcome');
        updateTutorialProgress('welcome');
      }
    }
  }, [userProfile]);

  const updateTutorialProgress = async (step) => {
    try {
      await profileAPI.updateProfile({
        tutorial_step: step,
        tutorial_completed: step === 'complete'
      });
      if (onUpdateProfile) {
        onUpdateProfile();
      }
    } catch (error) {
      // Failed to update tutorial progress
    }
  };

  const getStepInfo = () => {
    return tutorialSteps[currentStep] || tutorialSteps.welcome;
  };

  const handleNext = () => {
    const stepInfo = getStepInfo();
    if (stepInfo.nextStep) {
      stopAutoAdvanceObserver();
      setHighlightBounds(null); // Clear immediately
      setCurrentStep(stepInfo.nextStep);
      updateTutorialProgress(stepInfo.nextStep);
    } else {
      // Tutorial complete
      handleComplete();
    }
  };

  const handleBack = () => {
    stopAutoAdvanceObserver();
    
    // Find previous step
    const steps = Object.keys(tutorialSteps);
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStep = steps[currentIndex - 1];
      setHighlightBounds(null); // Clear immediately
      setCurrentStep(prevStep);
      updateTutorialProgress(prevStep);
    }
  };

  const handleSkip = () => {
    setShowSkipConfirm(true);
  };

  const confirmSkip = async () => {
    await updateTutorialProgress('complete');
    setIsVisible(false);
    setShowSkipConfirm(false);
  };

  const cancelSkip = () => {
    setShowSkipConfirm(false);
  };

  const handleComplete = async () => {
    await updateTutorialProgress('complete');
    setIsVisible(false);
  };

  // Update highlight when step changes or page changes
  useEffect(() => {
    const updateHighlight = () => {
      const stepInfo = getStepInfo();
      
      if (!stepInfo.highlightElement) {
        setHighlightBounds(null);
        return;
      }

      // Find element by various selectors
      const selectors = [
        `[data-tutorial="${stepInfo.highlightElement}"]`,
        `.${stepInfo.highlightElement}`,
        `#${stepInfo.highlightElement}`,
      ];

      let element = null;
      for (const selector of selectors) {
        try {
          const found = document.querySelector(selector);
          if (found) {
            element = found;
            break;
          }
        } catch (e) {
          // Invalid selector, continue
        }
      }

      const rect = element ? element.getBoundingClientRect() : null;
      const next = rect
        ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
        : null;

      // Keep the previous object when nothing moved: the observer below reacts
      // to DOM changes, and re-rendering the highlight would feed it again.
      setHighlightBounds((prev) => (sameBounds(prev, next) ? prev : next));
    };

    // One lookup now, one once the page had a chance to render the target.
    updateHighlight();
    const initialTimeout = setTimeout(updateHighlight, HIGHLIGHT_SETTLE_MS);

    // The highlighted element appears and disappears with the DOM, so watch
    // for that instead of re-querying the document on a timer.
    const observer =
      typeof MutationObserver === 'undefined' ? null : new MutationObserver(updateHighlight);
    if (observer) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-tutorial', 'class', 'id'],
      });
    }

    // Its position can also change without any DOM mutation.
    window.addEventListener('scroll', updateHighlight, true);
    window.addEventListener('resize', updateHighlight);

    return () => {
      clearTimeout(initialTimeout);
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', updateHighlight, true);
      window.removeEventListener('resize', updateHighlight);
    };
  }, [currentStep, currentPage]);

  // Check for page navigation
  useEffect(() => {
    const stepInfo = getStepInfo();
    if (stepInfo.requiresNavigation && currentPage === stepInfo.requiresNavigation) {
      // User navigated to required page, advance
      setTimeout(() => {
        handleNext();
      }, 500);
    }
  }, [currentPage, currentStep]);

  // Check for auto-advance conditions
  useEffect(() => {
    const stepInfo = getStepInfo();

    // Early return if no auto-advance needed
    if (stepInfo.autoAdvanceOn !== 'modal-open' && stepInfo.autoAdvanceOn !== 'modal-close') {
      return;
    }

    const waitsForOpen = stepInfo.autoAdvanceOn === 'modal-open';
    const advanceDelay = waitsForOpen ? MODAL_OPEN_ADVANCE_MS : MODAL_CLOSE_ADVANCE_MS;
    const firstCheckDelay = waitsForOpen ? MODAL_OPEN_FIRST_CHECK_MS : MODAL_CLOSE_FIRST_CHECK_MS;
    let advanceTimeout = null;

    // Advance once the modal the step waits on has appeared (or gone).
    const checkModal = () => {
      if (advanceTimeout) return;
      const modalCount = document.querySelectorAll(MODAL_SELECTORS).length;
      const satisfied = waitsForOpen ? modalCount > 0 : modalCount === 0;
      if (!satisfied) return;

      stopAutoAdvanceObserver();
      advanceTimeout = setTimeout(() => {
        advanceTimeout = null;
        handleNext();
      }, advanceDelay);
    };

    if (typeof MutationObserver !== 'undefined') {
      autoAdvanceObserverRef.current = new MutationObserver(checkModal);
      autoAdvanceObserverRef.current.observe(document.body, { childList: true, subtree: true });
    }
    // One initial check, after the same grace period the old poll gave the
    // step before it looked for the first time.
    const firstCheck = setTimeout(checkModal, firstCheckDelay);

    return () => {
      stopAutoAdvanceObserver();
      clearTimeout(firstCheck);
      if (advanceTimeout) clearTimeout(advanceTimeout);
    };
  }, [currentStep]);

  // Handle ESC key to skip
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isVisible && !showSkipConfirm) {
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, showSkipConfirm]);

  // Dragging handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.tutorial-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  if (!isVisible) {
    return null;
  }

  const stepInfo = getStepInfo();
  const canGoNext = !stepInfo.requiresNavigation || currentPage === stepInfo.requiresNavigation;

  return (
    <>
      {/* Dark overlay with cutout for highlight */}
      {highlightBounds && (
        <>
          {/* Top overlay */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: `${highlightBounds.top - 4}px`,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9998,
            pointerEvents: 'auto'
          }} />
          
          {/* Left overlay */}
          <div style={{
            position: 'fixed',
            top: `${highlightBounds.top - 4}px`,
            left: 0,
            width: `${highlightBounds.left - 4}px`,
            height: `${highlightBounds.height + 8}px`,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9998,
            pointerEvents: 'auto'
          }} />
          
          {/* Right overlay */}
          <div style={{
            position: 'fixed',
            top: `${highlightBounds.top - 4}px`,
            left: `${highlightBounds.left + highlightBounds.width + 4}px`,
            right: 0,
            height: `${highlightBounds.height + 8}px`,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9998,
            pointerEvents: 'auto'
          }} />
          
          {/* Bottom overlay */}
          <div style={{
            position: 'fixed',
            top: `${highlightBounds.top + highlightBounds.height + 4}px`,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9998,
            pointerEvents: 'auto'
          }} />
          
          {/* Highlight border */}
          <div className="tutorial-highlight" style={{
            top: highlightBounds.top - 4,
            left: highlightBounds.left - 4,
            width: highlightBounds.width + 8,
            height: highlightBounds.height + 8
          }} />
        </>
      )}

      {/* Tutorial box */}
      <div
        ref={tutorialRef}
        className={`tutorial-box ${isDragging ? 'dragging' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="tutorial-header">
          <h3>{stepInfo.title}</h3>
          <button className="tutorial-close" onClick={handleSkip} title="Skip Tutorial (ESC)">
            ✕
          </button>
        </div>
        
        <div className="tutorial-content">
          {stepInfo.content.split('\n').map((line, idx) => (
            <p key={idx}>{line}</p>
          ))}
        </div>

        <div className="tutorial-footer">
          <div className="tutorial-progress">
            Step {Math.max(0, Object.keys(tutorialSteps).indexOf(currentStep)) + 1} of {Object.keys(tutorialSteps).length}
          </div>
          <div className="tutorial-buttons">
            {currentStep !== 'welcome' && (
              <button className="tutorial-btn tutorial-btn-secondary" onClick={handleBack}>
                ← Back
              </button>
            )}
            {stepInfo.nextStep && canGoNext && (
              <button className="tutorial-btn tutorial-btn-primary" onClick={handleNext}>
                {stepInfo.requiresNavigation && currentPage !== stepInfo.requiresNavigation ? 'Waiting...' : 'Next →'}
              </button>
            )}
            {!stepInfo.nextStep && (
              <button className="tutorial-btn tutorial-btn-success" onClick={handleComplete}>
                Start Playing! 🎮
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Skip confirmation modal */}
      {showSkipConfirm && (
        <div className="tutorial-skip-modal-overlay">
          <div className="tutorial-skip-modal">
            <h3>Skip Tutorial?</h3>
            <p>Are you sure you want to skip the tutorial?</p>
            <p className="tutorial-warning">⚠️ You won't be able to restart it later.</p>
            <div className="tutorial-skip-buttons">
              <button className="tutorial-btn tutorial-btn-secondary" onClick={cancelSkip}>
                Cancel
              </button>
              <button className="tutorial-btn tutorial-btn-danger" onClick={confirmSkip}>
                Skip Tutorial
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Tutorial;
